import { PlayHubState } from "play/play-model"
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react"
import { INITIAL_STATE } from "play/initial-state"
import { produce } from "immer"
import {
  serverAddEventListener,
  ServerEvent,
  serverRemoveEventListener,
} from "utils/server-api"

export interface PlayContextState extends PlayHubState {
  modifyState(modifier: (state: PlayHubState) => void): void
}

export const PlayStateContext = createContext<PlayContextState>({
  ...INITIAL_STATE,
  modifyState: () => {},
})

let globalSetPlayStateFunc: Dispatch<SetStateAction<PlayContextState>> | null

// React may run a state update more than once: an urgent one (a click) that
// arrives while a less urgent one is waiting (the timer's, every animation
// frame; a poll's result) is applied first, then run again on top of the
// waiting one. Its state changes are meant to be repeated, a request it sends
// is not: every run of a keyboard answer sent the answer again. So a request
// started inside an update goes through oncePerUpdate, which remembers what
// each call returned on the update's first run and gives that back on the
// runs after it.
let s_currentUpdate: { results: unknown[]; next: number } | null = null

/**
 * Runs `effect` (a request, a timer) on the first run of the state update it
 * is called from, and returns that run's result on every later run; outside an
 * update it just runs it. Calls are matched by their order within the update.
 */
export function oncePerUpdate<T>(effect: () => T): T {
  const update = s_currentUpdate
  if (!update) return effect()

  const index = update.next++
  if (index < update.results.length) return update.results[index] as T

  const result = effect()
  update.results.push(result)
  return result
}

/** The updater for setState: applies `setter` to a draft, its effects once (see oncePerUpdate) */
function updateOnce(setter: (state: PlayHubState) => void) {
  const results: unknown[] = []

  return (prev: PlayContextState) =>
    produce(prev, (draft) => {
      const outer = s_currentUpdate
      s_currentUpdate = { results, next: 0 }
      try {
        setter(draft)
      } finally {
        s_currentUpdate = outer
      }
    })
}

export function globalModifyPlayState(setter: (state: PlayHubState) => void) {
  if (!globalSetPlayStateFunc) {
    console.error("globalSetPlayStateFunc is null")
    return
  }

  globalSetPlayStateFunc(updateOnce(setter))
}

export function PlayContextProvider(props: {
  children: ReactNode
  /** Changes the state the provider starts with; the play gallery presets a UI state with it */
  setup?: (state: PlayHubState) => void
}) {
  function modifyState(setter: (state: PlayHubState) => void) {
    setState(updateOnce(setter))
  }

  const [state, setState] = useState<PlayContextState>(() => {
    const initial: PlayContextState = { ...INITIAL_STATE, modifyState }
    const setup = props.setup
    return setup ? produce(initial, (draft) => setup(draft)) : initial
  })

  useEffect(() => {
    globalSetPlayStateFunc = setState
    return () => {
      globalSetPlayStateFunc = null
    }
  }, [])

  useEffect(() => {
    function listenForConnectionLost(event: ServerEvent) {
      if (
        event.type === "CONNECTION_LOST" &&
        !event.url.match(/\/pending-messages\//)
      ) {
        setState((prev) => ({
          ...prev,
          connectionLost: true,
        }))
      }
    }

    serverAddEventListener(listenForConnectionLost)
    return () => {
      serverRemoveEventListener(listenForConnectionLost)
    }
  }, [])

  return (
    <PlayStateContext.Provider value={state}>
      {props.children}
    </PlayStateContext.Provider>
  )
}
